import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Save, RotateCcw, Search, ShieldOff, Lock, CornerDownRight, Layers, BellRing } from "lucide-react";
import { toast } from "sonner";
import { NotificationPreferencesPanel } from "@/components/NotificationPreferencesPanel";

// Phase 84.44 — Founder-only Access Management. This is the ONLY place in the
// app where page/module access and action-level (edit/admin) permissions are
// managed — there is no duplicate permissions UI anywhere else (e.g. Team page).
// Dashboard isn't listed — it's always visible to everyone and can't be toggled off.
//
// Phase 84.60 — Categories now nest action-level permissions (Edit/CRUD/Admin)
// directly under the page they act on. Toggling a parent page ON/OFF cascades
// the same default to its nested action toggle(s), unless that action has
// already been explicitly diverged from the parent for that person — in which
// case the granular override wins.
const CATEGORIES = [
  {
    name: "Projects",
    items: [
      // Feb 2026 — Full De-Bundling pass. "Projects" used to be gated by ONE
      // master switch (projects_admin). It is now 3 co-equal, independently
      // toggleable actions (Create / Edit / Delete) — no action is ever
      // implied by another. Page visibility (seeing the Projects tab at all)
      // is its own separate row below, fully decoupled from these.
      { key: "my_work_projects", label: "Projects (page view)" },
      { key: "projects_create", label: "Create" },
      {
        key: "projects_edit", label: "Edit",
        children: [
          { key: "projects_edit_milestones", label: "Edit Milestones" },
          { key: "projects_add_deliverables", label: "Add Deliverables" },
          { key: "projects_update_goals", label: "Update Goals" },
        ],
      },
      { key: "projects_delete", label: "Delete" },
      {
        // Feb 2026 — Tasks Admin is DELIBERATELY separate from Projects Edit:
        // it governs assigning tasks to others + moving tasks to Done, WITHOUT
        // granting full project-record editing power.
        key: "tasks_admin", label: "Tasks Admin",
        children: [
          { key: "tasks_assign", label: "Assign to Others" },
          { key: "tasks_move_to_done", label: "Move Task to Done" },
        ],
      },
      { key: "my_work_reviews", label: "Approvals" },
      {
        key: "my_work_goals", label: "Goals",
        children: [
          { key: "goals_create", label: "Create" },
          { key: "goals_edit", label: "Edit" },
          { key: "goals_delete", label: "Delete" },
        ],
      },
      { key: "my_work_weekly_reports", label: "Weekly Reports",
        children: [{ key: "weekly_reports_admin", label: "Approve/Reject" }],
      },
      {
        key: "my_work_timesheet", label: "Timesheet",
        children: [{ key: "timesheet_admin", label: "Team-wide dashboard" }],
      },
      {
        key: "my_work_attendance", label: "Attendance",
        children: [{ key: "attendance_admin", label: "Team-wide + overrides" }],
      },
      { key: "client_inbox", label: "Client Inbox" },
    ],
  },
  {
    name: "People",
    items: [
      {
        key: "my_hr_leaves", label: "Leaves",
        children: [
          { key: "leaves_create", label: "Create Holiday" },
          { key: "leaves_edit", label: "Approve & Edit" },
          { key: "leaves_delete", label: "Delete Holiday" },
        ],
      },
      { key: "performance_reviews", label: "Performance" },
      {
        key: "my_hr_documents", label: "My Documents",
        children: [{ key: "hr_docs_admin", label: "View all employees'" }],
      },
      { key: "my_hr_assets", label: "Assets" },
      {
        key: "my_hr_team_view", label: "Team (page view)",
        children: [
          { key: "team_create", label: "Create" },
          { key: "team_edit", label: "Edit" },
          { key: "team_delete", label: "Delete" },
        ],
      },
      { key: "team_board_view", label: "Team Board" },
      {
        key: "recruitment", label: "Recruitment (page view)",
        children: [
          { key: "recruitment_create", label: "Create" },
          { key: "recruitment_edit", label: "Edit" },
          { key: "recruitment_admin", label: "Delete Candidates" },
        ],
      },
      { key: "grievances_view", label: "Grievances" },
    ],
  },
  {
    name: "Studio",
    items: [
      // Feb 2026 — bare visibility of the Studio nav item, independent of
      // finance/studio_taxonomy/audit_log (each of those still gates its
      // own sub-tab's actual content once inside).
      { key: "studio_view", label: "Studio (page view)" },
      { key: "finance", label: "Finance (page view)" },
      { key: "finance_create", label: "Create" },
      {
        key: "finance_edit", label: "Edit",
        children: [
          { key: "finance_approve_purchases", label: "Approve Purchase Requests" },
          { key: "finance_edit_tax_rates", label: "Edit FX/Tax Rates" },
        ],
      },
      {
        key: "finance_delete", label: "Delete",
        children: [
          { key: "finance_manage_payouts", label: "Pending Payments (Delete/Reject)" },
        ],
      },
      { key: "studio_taxonomy", label: "Taxonomy" },
      { key: "audit_log", label: "Audit Log" },
      {
        key: "clients_admin", label: "Clients (page view)",
        children: [
          { key: "clients_create", label: "Create" },
          { key: "clients_edit", label: "Edit" },
          { key: "clients_delete", label: "Delete" },
        ],
      },
      {
        key: "vendors_admin", label: "Vendors",
        children: [
          { key: "vendors_onboard", label: "Onboard New Vendor" },
          { key: "vendors_edit", label: "Edit Vendor Details" },
          { key: "vendors_delete", label: "Delete Vendor" },
        ],
      },
      {
        key: "studio_assets", label: "Asset Register (page view)",
        children: [
          { key: "assets_create", label: "Create" },
          { key: "assets_edit", label: "Edit" },
          { key: "assets_delete", label: "Delete" },
        ],
      },
      {
        key: "studio_purchases", label: "Purchases (page view)",
        children: [
          { key: "purchases_create", label: "Create" },
          { key: "purchases_edit", label: "Edit" },
          { key: "purchases_delete", label: "Delete" },
        ],
      },
      { key: "approvals_admin", label: "Onboarding Approvals" },
      { key: "marketing_admin", label: "Marketing Intel" },
      { key: "branding_admin", label: "Manage Public Branding" },
    ],
  },
  {
    name: "CRM / Registry / Vault / Chat",
    items: [
      {
        key: "crm_access", label: "CRM",
        children: [
          { key: "crm_edit", label: "Edit" },
          { key: "crm_admin", label: "Admin" },
        ],
      },
      {
        key: "contracts_admin", label: "Contracts (page view)",
        children: [
          { key: "contracts_create", label: "Create" },
          { key: "contracts_edit", label: "Edit" },
          { key: "contracts_delete", label: "Delete" },
        ],
      },
      {
        key: "registry_view", label: "Registry",
        children: [{ key: "registry_manage_roster", label: "Manage Team Roster" }],
      },
      {
        key: "vault_view", label: "Vault",
        children: [{ key: "vault_admin", label: "See others' entries" }],
      },
      {
        key: "chat_view", label: "Chat",
        children: [{ key: "chat_create_channel", label: "New channel" }],
      },
    ],
  },
  {
    name: "Legacy / Danger zone",
    items: [
      { key: "reset_demo_data", label: "Reset demo data" },
      { key: "personal_goals", label: "Goals (legacy)" },
      { key: "weekly_reports", label: "Weekly Reports (legacy)" },
      { key: "system_admin_tools", label: "Digest/reminder triggers + housekeeping" },
      { key: "team_admin", label: "Legacy admin-tier signal (global notification feed + Google Sheets sync flag only — no longer gates Team CRUD)" },
    ],
  },
];

// Flattened column list, preserving category order, with each parent's
// children rendered immediately after it. Each category also gets a
// `featureKeys` list (for the per-person master toggle) and its own
// `columns` sub-list (used to render the master column + its cells).
CATEGORIES.forEach((cat) => {
  cat.featureKeys = cat.items.flatMap((it) => [it.key, ...(it.children || []).map((c) => c.key)]);
  cat.columns = cat.items.flatMap((item, idx) => [
    { key: item.key, label: item.label, isChild: false, isFirstInCat: idx === 0 },
    ...((item.children || []).map((child) => ({
      key: child.key, label: child.label, isChild: true, parentKey: item.key, parentLabel: item.label,
    }))),
  ]);
});
const FLAT_COLUMNS = CATEGORIES.flatMap((cat) => cat.columns);
const ALL_FEATURES = FLAT_COLUMNS.map((c) => c.key);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// parentKey -> [childKeys], for cascading defaults.
const CHILDREN_OF = {};
// childKey -> parentKey — the inverse, so a direct click on a child column
// can be recognised (as opposed to a cascade write triggered by its parent).
const PARENT_OF = {};
CATEGORIES.forEach((cat) => cat.items.forEach((item) => {
  if (item.children?.length) {
    CHILDREN_OF[item.key] = item.children.map((c) => c.key);
    item.children.forEach((c) => { PARENT_OF[c.key] = item.key; });
  }
}));

export default function AccessManagement() {
  const { user: current } = useAuth();

  if (current?.role !== "founder") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="access-management-denied">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
            <ShieldOff size={22} className="text-red-500" />
          </div>
          <div className="text-lg font-semibold text-slate-800 mb-1">Access denied</div>
          <p className="text-sm text-slate-500">Access Management is restricted to the Founder role only.</p>
        </div>
      </div>
    );
  }
  return <AccessManagementInner />;
}

function AccessManagementInner() {
  const [users, setUsers] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [overrides, setOverrides] = useState({}); // { uid: { feature: bool } } — staged
  const [initial, setInitial] = useState({});     // server snapshot
  // { uid: Set<childKey> } — child columns the founder has explicitly clicked
  // directly THIS SESSION. Pinned children are never re-cascaded by their
  // parent, so a granular override survives repeated parent ON/OFF cycles.
  // Session-scoped by design (resets on reload) — the underlying override
  // value itself is still saved/persisted as normal.
  const [pinned, setPinned] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: perm }, { data: u }] = await Promise.all([
        api.get("/permissions/features"),
        api.get("/users"),
      ]);
      setRoleDefaults(perm.role_defaults || {});
      setUsers((u || []).filter((x) => x.role !== "client" && x.role !== "vendor" && !x.merged_into));
      const seed = {};
      for (const row of u || []) seed[row.id] = { ...(row.permission_overrides || {}) };
      setOverrides(seed);
      setInitial(JSON.parse(JSON.stringify(seed)));
      setPinned({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  // Reads from a given overrides snapshot (defaults to live staged state).
  const effectiveForIn = (ov, uid, feature, role) => {
    if (role === "founder") return true;
    const uOv = ov[uid] || {};
    if (uOv[feature] !== undefined) return uOv[feature];
    return !!(roleDefaults[role] || {})[feature];
  };
  const effectiveFor = (uid, feature, role) => effectiveForIn(overrides, uid, feature, role);

  const setFeature = (ov, uid, feature, role, value) => {
    const dfl = !!(roleDefaults[role] || {})[feature];
    const nextOv = { ...(ov[uid] || {}) };
    if (value === dfl) delete nextOv[feature]; else nextOv[feature] = value;
    return { ...ov, [uid]: nextOv };
  };

  const isPinned = (uid, key) => (pinned[uid] || new Set()).has(key);
  const pin = (uid, key) => setPinned((prev) => {
    const next = new Set(prev[uid] || []);
    next.add(key);
    return { ...prev, [uid]: next };
  });

  const toggle = (uid, feature, role) => {
    if (role === "founder") return; // cannot be disabled, per the founder-override requirement
    const oldEff = effectiveFor(uid, feature, role);
    const next = !oldEff;
    let nextOv = setFeature(overrides, uid, feature, role, next);

    // A direct click on a child column is itself the "override at a more
    // granular level" — pin it so its parent never re-cascades over it again.
    if (PARENT_OF[feature]) {
      pin(uid, feature);
    }

    // Cascade to nested action toggle(s): only those NOT pinned (i.e. never
    // explicitly diverged from their parent by the founder this session).
    const kids = CHILDREN_OF[feature] || [];
    for (const kid of kids) {
      if (isPinned(uid, kid)) continue;
      nextOv = setFeature(nextOv, uid, kid, role, next);
    }
    setOverrides(nextOv);
  };

  const dirtyUids = useMemo(() => {
    const out = [];
    for (const uid of Object.keys(overrides)) {
      if (JSON.stringify(overrides[uid] || {}) !== JSON.stringify(initial[uid] || {})) out.push(uid);
    }
    return out;
  }, [overrides, initial]);

  const resetChanges = () => setOverrides(JSON.parse(JSON.stringify(initial)));

  const saveAll = async () => {
    if (dirtyUids.length === 0) return;
    setSaving(true);
    try {
      const results = await Promise.allSettled(dirtyUids.map((uid) =>
        api.put(`/users/${uid}/permissions`, { overrides: overrides[uid] || {} }).then(() => uid)
      ));
      const succeeded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failed = results.length - succeeded.length;
      if (succeeded.length > 0) {
        setInitial((prevInitial) => {
          const nextInitial = { ...prevInitial };
          for (const uid of succeeded) nextInitial[uid] = JSON.parse(JSON.stringify(overrides[uid] || {}));
          return nextInitial;
        });
      }
      if (failed > 0) toast.error(`Saved ${succeeded.length}, ${failed} failed — retry those`);
      else toast.success(`Saved access changes for ${succeeded.length} ${succeeded.length === 1 ? "person" : "people"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Grant/revoke one feature for every currently-visible (search-filtered), non-founder person.
  // Cascades to nested action toggle(s) with the same pinned-aware rule as toggle().
  const bulkColumn = (feature, value) => {
    let next = { ...overrides };
    const kids = CHILDREN_OF[feature] || [];
    const isChildColumn = !!PARENT_OF[feature];
    for (const u of filteredUsers) {
      if (u.role === "founder") continue;
      next = setFeature(next, u.id, feature, u.role, value);
      if (isChildColumn) pin(u.id, feature);
      for (const kid of kids) {
        if (isPinned(u.id, kid)) continue;
        next = setFeature(next, u.id, kid, u.role, value);
      }
    }
    setOverrides(next);
  };

  // Per-person category master toggle — turns every item (and its non-pinned
  // children) in a category ON together, or OFF together, for one person.
  const categoryAllOn = (uid, cat, role) => cat.featureKeys.every((k) => effectiveFor(uid, k, role));

  const toggleCategory = (uid, cat, role) => {
    if (role === "founder") return;
    const turnOn = !categoryAllOn(uid, cat, role);
    let nextOv = overrides;
    for (const item of cat.items) {
      nextOv = setFeature(nextOv, uid, item.key, role, turnOn);
      for (const child of (item.children || [])) {
        if (isPinned(uid, child.key)) continue;
        nextOv = setFeature(nextOv, uid, child.key, role, turnOn);
      }
    }
    setOverrides(nextOv);
  };

  const bulkCategory = (cat, value) => {
    let next = { ...overrides };
    for (const u of filteredUsers) {
      if (u.role === "founder") continue;
      for (const item of cat.items) {
        next = setFeature(next, u.id, item.key, u.role, value);
        for (const child of (item.children || [])) {
          if (isPinned(u.id, child.key)) continue;
          next = setFeature(next, u.id, child.key, u.role, value);
        }
      }
    }
    setOverrides(next);
  };

  // Permission templates — founder-saved snapshots of a full permission map,
  // applied to any person in one click (persists immediately, replacing
  // that person's overrides entirely — independent of the staged/Save flow).
  const [templates, setTemplates] = useState([]);
  const [templateDialogUid, setTemplateDialogUid] = useState(null); // uid currently picking/saving a template
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [activeTab, setActiveTab] = useState("permissions");

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/permission-templates");
      setTemplates(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadTemplates(); }, []);

  const saveAsTemplate = async (uid, role, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    setSavingTemplate(true);
    try {
      const snapshot = {};
      for (const f of ALL_FEATURES) snapshot[f] = effectiveFor(uid, f, role);
      const { data } = await api.post("/permission-templates", { name: trimmed, overrides: snapshot });
      setTemplates((prev) => [data, ...prev]);
      toast.success(`Saved template "${trimmed}"`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingTemplate(false); }
  };

  const applyTemplate = async (uid, templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    setApplyingTemplate(true);
    try {
      const { data } = await api.put(`/users/${uid}/permissions`, { overrides: tpl.overrides || {} });
      const applied = data.permission_overrides || {};
      setOverrides((prev) => ({ ...prev, [uid]: applied }));
      setInitial((prev) => ({ ...prev, [uid]: JSON.parse(JSON.stringify(applied)) }));
      setPinned((prev) => ({ ...prev, [uid]: new Set() }));
      toast.success(`Applied template "${tpl.name}"`);
      setTemplateDialogUid(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplyingTemplate(false); }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await api.delete(`/permission-templates/${templateId}`);
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
      toast.success("Template deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-full mx-auto fade-in" data-testid="access-management-page">
      <PageHeader
        eyebrow="Founder only"
        title="Access Management"
        subtitle={activeTab === "permissions"
          ? "The single home for every page and action permission in the app. Every page is OFF by default for everyone except Founders. Nested toggles (indented, with ↳) follow their parent page unless you override them individually. Editing a Registry employee's Monthly Seat Cost always stays Founder-only — it's intentionally not delegable and isn't shown as a toggle here."
          : "Control which actions still email people, per role. Everything always creates an in-app notification — the toggle only decides whether it ALSO sends a real email."}
        actions={activeTab === "permissions" ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={resetChanges} disabled={dirtyUids.length === 0 || saving} data-testid="access-mgmt-discard">
              <RotateCcw size={14} className="mr-1.5" />Discard
            </Button>
            <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={saveAll} disabled={dirtyUids.length === 0 || saving} data-testid="access-mgmt-save">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save {dirtyUids.length > 0 ? `(${dirtyUids.length})` : ""}
            </Button>
          </div>
        ) : null}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
        <TabsList className="rounded-sm" data-testid="access-mgmt-tabs">
          <TabsTrigger value="permissions" className="rounded-sm" data-testid="access-mgmt-tab-permissions">
            <Layers size={13} className="mr-1.5" />Permissions
          </TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-sm" data-testid="access-mgmt-tab-notifications">
            <BellRing size={13} className="mr-1.5" />Notification Preferences
          </TabsTrigger>
        </TabsList>

        <TabsContent value="permissions" className="mt-3">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="relative w-72">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email or role…"
            className="pl-7 rounded-sm"
            data-testid="access-mgmt-search"
          />
        </div>
        <div className="text-xs text-slate-500" data-testid="access-mgmt-dirty-count">
          {dirtyUids.length > 0 ? `${dirtyUids.length} ${dirtyUids.length === 1 ? "person" : "people"} with unsaved changes` : "No unsaved changes"}
        </div>
      </div>

      <div className="overflow-auto border border-slate-200 rounded-sm" style={{ maxHeight: "72vh" }} data-testid="access-mgmt-table-wrap">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-500">
            <Loader2 size={18} className="animate-spin mr-2" /> Loading Access Management…
          </div>
        ) : (
          <table className="min-w-full text-xs" data-testid="access-mgmt-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th rowSpan={2} className="text-left px-3 py-2 sticky left-0 bg-slate-50 min-w-[200px] border-r border-slate-200 align-bottom">
                  Team member
                </th>
                {CATEGORIES.map((cat) => (
                  <th key={cat.name} colSpan={cat.columns.length + 1} className="px-2 py-1.5 text-center font-semibold text-slate-600 border-l border-slate-200 whitespace-nowrap">
                    {cat.name}
                  </th>
                ))}
              </tr>
              <tr>
                {CATEGORIES.map((cat) => (
                  <React.Fragment key={cat.name}>
                    <th className="px-2 py-2 text-center whitespace-nowrap font-semibold text-slate-600 bg-slate-100 border-l border-slate-300">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-[10px] uppercase tracking-wide">All</span>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => bulkCategory(cat, true)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`access-mgmt-bulk-on-cat-${slug(cat.name)}`}>all</button>
                          <span className="text-slate-300">·</span>
                          <button type="button" onClick={() => bulkCategory(cat, false)} className="text-[10px] text-red-600 hover:underline" data-testid={`access-mgmt-bulk-off-cat-${slug(cat.name)}`}>none</button>
                        </div>
                      </div>
                    </th>
                    {cat.columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-2 py-2 text-center whitespace-nowrap font-medium ${col.isChild ? "bg-slate-50/80 text-slate-500" : "text-slate-700"}`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`max-w-[110px] truncate flex items-center gap-1 ${col.isChild ? "italic" : ""}`}
                            title={col.isChild ? `${col.label} — nested under ${col.parentLabel}` : col.label}
                          >
                            {col.isChild && <CornerDownRight size={10} className="text-slate-400 shrink-0" />}
                            {col.label}
                          </span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => bulkColumn(col.key, true)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`access-mgmt-bulk-on-${col.key}`}>all</button>
                            <span className="text-slate-300">·</span>
                            <button type="button" onClick={() => bulkColumn(col.key, false)} className="text-[10px] text-red-600 hover:underline" data-testid={`access-mgmt-bulk-off-${col.key}`}>none</button>
                          </div>
                        </div>
                      </th>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isFounderRow = u.role === "founder";
                const isDirty = dirtyUids.includes(u.id);
                return (
                  <tr key={u.id} className={`border-t border-slate-200 hover:bg-slate-50/60 ${isDirty ? "bg-orange-50/40" : ""} ${isFounderRow ? "bg-amber-50/50" : ""}`} data-testid={`access-mgmt-row-${u.id}`}>
                    <td className="px-3 py-1.5 sticky left-0 bg-white border-r border-slate-200 z-[1]">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        {u.name || u.email}
                        {isFounderRow && <Lock size={11} className="text-amber-600" title="Founder — always full access, cannot be edited" />}
                        {!isFounderRow && (
                          <button
                            type="button"
                            onClick={() => setTemplateDialogUid(u.id)}
                            className="text-slate-400 hover:text-[#F54800]"
                            title="Save/apply a permission template"
                            data-testid={`access-mgmt-template-btn-${u.id}`}
                          >
                            <Layers size={11} />
                          </button>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 capitalize">{u.role} · {u.business_unit || "—"}</div>
                    </td>
                    {CATEGORIES.map((cat) => (
                      <React.Fragment key={cat.name}>
                        <td className="px-2 py-1.5 text-center bg-slate-100/50 border-l border-slate-300" data-testid={`access-mgmt-cat-cell-${u.id}-${slug(cat.name)}`}>
                          <label className={`inline-flex items-center relative ${isFounderRow ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
                            <Checkbox
                              checked={isFounderRow || categoryAllOn(u.id, cat, u.role)}
                              disabled={isFounderRow}
                              onCheckedChange={() => toggleCategory(u.id, cat, u.role)}
                              aria-label={`${u.name}: all ${cat.name}`}
                            />
                          </label>
                        </td>
                        {cat.columns.map((col) => {
                          const f = col.key;
                          const eff = effectiveFor(u.id, f, u.role);
                          const dfl = !!(roleDefaults[u.role] || {})[f];
                          const isOverride = !isFounderRow && (overrides[u.id] || {})[f] !== undefined && (overrides[u.id] || {})[f] !== dfl;
                          return (
                            <td key={f} className={`px-2 py-1.5 text-center ${col.isChild ? "bg-slate-50/50" : ""}`} data-testid={`access-mgmt-cell-${u.id}-${f}`}>
                              <label className={`inline-flex items-center relative ${isFounderRow ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
                                <Checkbox checked={eff} disabled={isFounderRow} onCheckedChange={() => toggle(u.id, f, u.role)} aria-label={`${u.name}: ${f}`} />
                                {isOverride && <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-[#F54800]" aria-label="override" />}
                              </label>
                            </td>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={ALL_FEATURES.length + CATEGORIES.length + 1} className="p-6 text-center text-slate-500">No one matches &quot;{query}&quot;.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {templateDialogUid && (
        <TemplateDialog
          uid={templateDialogUid}
          user={users.find((u) => u.id === templateDialogUid)}
          templates={templates}
          onClose={() => setTemplateDialogUid(null)}
          onSave={saveAsTemplate}
          onApply={applyTemplate}
          onDelete={deleteTemplate}
          saving={savingTemplate}
          applying={applyingTemplate}
        />
      )}
        </TabsContent>

        <TabsContent value="notifications" className="mt-3">
          <NotificationPreferencesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TemplateDialog({ uid, user, templates, onClose, onSave, onApply, onDelete, saving, applying }) {
  const [name, setName] = useState("");
  if (!user) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="template-dialog-backdrop">
      <div className="bg-white rounded-sm max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="template-dialog">
        <div className="font-display font-bold text-slate-900 mb-1">Permission templates — {user.name}</div>
        <p className="text-xs text-slate-500 mb-4">Save {user.name}&apos;s current on-screen access as a reusable template, or apply a saved template to replace their access instantly.</p>

        <div className="mb-4">
          <div className="text-xs font-medium text-slate-700 mb-1.5">Save current access as…</div>
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Designer starter kit"
              className="rounded-sm text-sm"
              data-testid="template-name-input"
            />
            <Button
              size="sm"
              className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white shrink-0"
              disabled={saving || !name.trim()}
              onClick={() => { onSave(uid, user.role, name); setName(""); }}
              data-testid="template-save-btn"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-3">
          <div className="text-xs font-medium text-slate-700 mb-1.5">Apply a saved template</div>
          {templates.length === 0 ? (
            <p className="text-xs text-slate-400" data-testid="template-empty-state">No templates saved yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-sm border border-slate-200 hover:bg-slate-50" data-testid={`template-row-${t.id}`}>
                  <span className="text-sm text-slate-800 truncate">{t.name}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" disabled={applying} onClick={() => onApply(uid, t.id)} data-testid={`template-apply-${t.id}`}>Apply</Button>
                    <button type="button" className="text-slate-400 hover:text-red-600 text-xs" onClick={() => onDelete(t.id)} data-testid={`template-delete-${t.id}`}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="template-dialog-close">Close</Button>
        </div>
      </div>
    </div>
  );
}
